import { useRef, useState } from 'react';
import {
  useAddAttachments, useDeleteReceipt, useRemoveAttachment, useUpdateReceipt,
} from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import {
  Avatar, ConfirmDialog, Field, Modal, MoneyInput, SectionHead, StatLine,
} from '@/components/ui';
import { IconCheck, IconEdit, IconPlus, IconTrash } from '@/components/Icons';
import { formatBRL, formatDate, LEVERAGE_LONG_LABELS, LEVERAGE_STATUS_LABELS, TRANSFER_LABELS } from '@/lib/format';
import { ACCEPT_ATTRIBUTE, MAX_FILES, prepareFiles } from '@/lib/files';
import {
  countsAsSold,
  isAlavancagem,
  isDeposit,
  isGas,
  isReviewed,
  isTransfer,
  isUnaDeposit,
  ownValueOf,
  REVIEW_CHIP,
  REVIEW_LABELS,
  reviewViewOf,
  soughtOf,
} from '@/lib/receipts';
import type { LeverageStatus, Receipt, ReceiptAttachment, ReceiptSource, TransferStatus } from '@/lib/types';
import { ReviewPanel } from '@/components/review/ReviewPanel';
import { AttachmentTile } from './AttachmentTile';
import { ClassifyDepositPanel } from './ClassifyDepositPanel';
import { draftFromReceipt, ReceiptFields, validateDraft, type ReceiptDraft } from './ReceiptFields';

interface ReceiptDetailModalProps {
  receipt: Receipt;
  onClose: () => void;
}

/** Linha de apoio do repasse, conforme a situação. */
/** Nome da origem de um comprovante que não foi anexado à mão. */
const IMPORTED_LABELS: Record<ReceiptSource, string> = {
  EPHRA: 'Ephra',
  CAKTO: 'Cakto',
  KIRVANO: 'Kirvano',
  DOTPAG: 'DotPag',
  MUNDPAY: 'MundPay',
  CRYPTO: 'Cripto',
  MANUAL: '',
};

const TRANSFER_SUBTITLE: Record<TransferStatus, string> = {
  DONE: 'Repasse feito',
  PENDING: 'Ainda não foi feito',
  NONE: 'Este comprovante não tem repasse',
};

/** Linha de apoio e cor da situação da alavancagem. */
const LEVERAGE_STATUS_VIEW: Record<LeverageStatus, { sub: string; color: string }> = {
  PENDING: { sub: 'Ainda não foi feita', color: 'var(--warning)' },
  DONE: { sub: 'Alavancagem feita', color: 'var(--success)' },
  FAILED: { sub: 'Não deu para fazer', color: 'var(--danger)' },
};

/** Os botões da alavancagem: cada um leva a uma das outras situações. */
const LEVERAGE_ACTIONS: Record<LeverageStatus, { label: string; done: string }> = {
  DONE: { label: 'Marcar como realizada', done: 'Alavancagem marcada como realizada.' },
  FAILED: { label: 'Não realizada', done: 'Alavancagem marcada como não realizada.' },
  PENDING: { label: 'Voltar para pendente', done: 'A alavancagem voltou para pendente.' },
};

/**
 * Detalhe do comprovante: os arquivos, a situação do repasse e a edição.
 * Guarda a própria cópia do comprovante — o detalhe continua aberto mesmo
 * quando a alteração tira o comprovante do filtro da lista.
 */
export function ReceiptDetailModal({ receipt: initial, onClose }: ReceiptDetailModalProps) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const updateReceipt = useUpdateReceipt();
  const deleteReceipt = useDeleteReceipt();
  const addAttachments = useAddAttachments();
  const removeAttachment = useRemoveAttachment();
  const fileInput = useRef<HTMLInputElement>(null);

  const [receipt, setReceipt] = useState(initial);
  const [editing, setEditing] = useState(false);
  /** Parcela paga de uma venda em cripto: valor, data e dono vêm do carnê. */
  const crypto = receipt.source === 'CRYPTO';
  /** Pedido do checkout (Ephra, Cakto, Kirvano) ou parcela em cripto: valor, data, tipo e dono não mudam aqui. */
  const imported = receipt.source !== 'MANUAL';
  /** De onde veio, no selo e no aviso: "Cripto", "Cakto"… */
  const importedFrom = IMPORTED_LABELS[receipt.source];
  const [draft, setDraft] = useState<ReceiptDraft>(() => draftFromReceipt(initial));
  const [error, setError] = useState<string | null>(null);
  const [markingTransfer, setMarkingTransfer] = useState(false);
  const [pulled, setPulled] = useState(0);
  /** Marcando a alavancagem como realizada: é quando entra a buscada. */
  const [markingDone, setMarkingDone] = useState(false);
  const [sought, setSought] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const count = receipt.attachments.length;
  const saving = updateReceipt.isPending;
  /** Gás do cliente: sem alavancagem, sem repasse, fora do vendido. */
  const gas = isGas(receipt.leverageType);
  /** Comprovante de repasse: só o repasse, pendente ou feito — nunca "sem repasse". */
  const transfer = isTransfer(receipt.leverageType);
  /** Depósito com alavancagem: depositado, situação da alavancagem (separada do repasse) e, realizada, a buscada. */
  const alavancagem = isAlavancagem(receipt.leverageType);
  const leverageStatus: LeverageStatus = receipt.leverageStatus ?? 'PENDING';
  const soughtAmount = soughtOf(receipt);

  async function save(body: Record<string, unknown>, message: string): Promise<boolean> {
    try {
      const { receipt: updated } = await updateReceipt.mutateAsync({ id: receipt.id, ...body });
      setReceipt(updated);
      toast.success(message);
      return true;
    } catch (caught) {
      toast.fromError(caught);
      return false;
    }
  }

  function startEditing() {
    setDraft(draftFromReceipt(receipt));
    setError(null);
    setMarkingTransfer(false);
    setMarkingDone(false);
    setEditing(true);
  }

  async function saveEdit() {
    // Comprovante antigo, sem tipo: dá para corrigir o resto sem ser obrigado a escolher.
    const typeChanged = draft.leverageType !== (receipt.leverageType ?? '');
    // A realizada que segue realizada com a mesma buscada não manda a buscada: a antiga, sem ela, segue editável.
    const keepSought =
      isAlavancagem(draft.leverageType) && isAlavancagem(receipt.leverageType) &&
      draft.leverageStatus === 'DONE' && receipt.leverageStatus === 'DONE' && draft.soldAmount === receipt.soldAmount;
    // Os antigos sem cliente ou sem depositado seguem editáveis: a regra vale quando o campo ou o tipo muda.
    const problem = validateDraft(draft, {
      requireMember: false,
      requireType: receipt.leverageType !== null,
      requireLead: typeChanged || draft.leadId !== (receipt.leadId ?? ''),
      requireDeposit: typeChanged || draft.depositAmount !== receipt.depositAmount,
      requireSought: !keepSought,
      originalNote: receipt.note ?? '',
    });
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    // Venda de checkout: só o que é da operação muda aqui (o resto vem da plataforma).
    if (imported) {
      const saved = await save(
        { leadId: draft.leadId || null, note: draft.note.trim() || null },
        'Comprovante atualizado.',
      );
      if (saved) setEditing(false);
      return;
    }
    if (isTransfer(draft.leverageType)) {
      const saved = await save(transferEdit(), 'Comprovante atualizado.');
      if (saved) setEditing(false);
      return;
    }
    const saved = await save(
      {
        description: draft.description.trim(),
        ...(keepSought ? {} : { soldAmount: draft.soldAmount }),
        ...(draft.leverageType ? { leverageType: draft.leverageType } : {}),
        // Virar gás ou depósito leva junto: sem alavancagem e sem repasse (o servidor confere o gás).
        ...(isGas(draft.leverageType) || isDeposit(draft.leverageType) || isUnaDeposit(draft.leverageType)
          ? { leverageAmount: 0, transferStatus: 'NONE' }
          : { leverageAmount: draft.leverageAmount }),
        ...(isAlavancagem(draft.leverageType)
          ? { depositAmount: draft.depositAmount, leverageStatus: draft.leverageStatus }
          : {}),
        date: draft.date,
        time: draft.time,
        leadId: draft.leadId || null,
        note: draft.note.trim() || null,
        ...(isAdmin && draft.memberId && draft.memberId !== receipt.memberId ? { memberId: draft.memberId } : {}),
      },
      'Comprovante atualizado.',
    );
    if (saved) setEditing(false);
  }

  /**
   * A edição de um repasse: os outros campos nem aparecem, então só vão o tipo
   * e o membro. Quem vira repasse perde a alavancagem e o valor vendido, e o
   * "sem repasse" vira pendente.
   */
  function transferEdit(): Record<string, unknown> {
    const member = isAdmin && draft.memberId && draft.memberId !== receipt.memberId ? { memberId: draft.memberId } : {};
    if (transfer) return { description: draft.description.trim() || 'Repasse', ...member };
    return {
      description: draft.description.trim() || 'Repasse',
      leverageType: 'LIVE',
      leverageAmount: 0,
      soldAmount: receipt.transferStatus === 'DONE' ? receipt.transferAmount : 0,
      ...(receipt.transferStatus === 'NONE' ? { transferStatus: 'PENDING' } : {}),
      ...member,
    };
  }

  async function confirmLeverageDone() {
    if (await save({ leverageStatus: 'DONE', soldAmount: sought }, LEVERAGE_ACTIONS.DONE.done)) {
      setMarkingDone(false);
    }
  }

  async function confirmTransfer() {
    if (await save({ transferStatus: 'DONE', transferAmount: pulled }, 'Repasse marcado como feito.')) {
      setMarkingTransfer(false);
    }
  }

  async function upload(list: File[]) {
    if (list.length === 0) return;
    setUploading(true);
    try {
      const { files, problems } = await prepareFiles(list, MAX_FILES - count);
      problems.forEach((problem) => toast.error(problem));
      if (files.length === 0) return;
      const form = new FormData();
      files.forEach((file) => form.append('files', file, file.name));
      const { receipt: updated } = await addAttachments.mutateAsync({ id: receipt.id, form });
      setReceipt(updated);
      toast.success(files.length === 1 ? 'Arquivo anexado.' : `${files.length} arquivos anexados.`);
    } catch (caught) {
      toast.fromError(caught);
    } finally {
      setUploading(false);
    }
  }

  async function detach(attachment: ReceiptAttachment) {
    try {
      const { receipt: updated } = await removeAttachment.mutateAsync({ id: receipt.id, attachmentId: attachment.id });
      setReceipt(updated);
      toast.success(`"${attachment.fileName}" removido.`);
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function confirmDelete() {
    try {
      await deleteReceipt.mutateAsync(receipt.id);
      toast.success('Comprovante excluído.');
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  /** Os botões do repasse — no bloco do repasse, ao lado da alavancagem, ou sozinhos nos outros tipos. */
  const transferActions = markingTransfer ? (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ width: 210 }}>
        <Field label="Repasse puxado (R$)">
          <MoneyInput value={pulled} onChange={setPulled} autoFocus onEnter={() => void confirmTransfer()} />
        </Field>
      </div>
      <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void confirmTransfer()}>
        {saving ? <span className="spinner" /> : <IconCheck />} Confirmar repasse
      </button>
      <button type="button" className="btn btn-ghost" onClick={() => setMarkingTransfer(false)}>Cancelar</button>
    </div>
    ) : (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
      {receipt.transferStatus !== 'DONE' ? (
        <button
          type="button"
          className="btn"
          onClick={() => {
            setPulled(0);
            setMarkingTransfer(true);
          }}
        >
          <IconCheck /> Marcar repasse como feito
        </button>
      ) : null}
      {receipt.transferStatus !== 'PENDING' ? (
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          disabled={saving}
          onClick={() =>
            void save(
              { transferStatus: 'PENDING' },
              receipt.transferStatus === 'DONE' ? 'O repasse voltou para pendente.' : 'Repasse marcado como pendente.',
            )
          }
        >
          {receipt.transferStatus === 'DONE' ? 'Voltar para pendente' : 'Tem repasse a fazer'}
        </button>
      ) : null}
      {receipt.transferStatus !== 'NONE' && !transfer ? (
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          disabled={saving}
          onClick={() => void save({ transferStatus: 'NONE' }, 'Comprovante marcado como sem repasse.')}
        >
          Sem repasse
        </button>
      ) : null}
    </div>
  );

  // A confirmação toma o lugar do detalhe: dois modais empilhados fechariam juntos no Esc.
  if (confirmingDelete) {
    return (
      <ConfirmDialog
        title="Excluir comprovante"
        danger
        confirmLabel="Excluir"
        busy={deleteReceipt.isPending}
        message={
          <>
            {transfer ? (
              <>Excluir o comprovante de repasse ({receipt.description})? Os arquivos saem junto e o repasse deixa de contar.</>
            ) : (
              <>
                Excluir o comprovante de <strong>{formatBRL(ownValueOf(receipt))}</strong> ({receipt.description})?{' '}
                {gas
                  ? 'Os arquivos saem junto e o valor sai do bloco do gás.'
                  : 'Os arquivos saem junto e a venda deixa de contar no ranking e nas metas.'}
              </>
            )}
          </>
        }
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmingDelete(false)}
      />
    );
  }

  return (
    <Modal
      title={editing ? 'Editar comprovante' : 'Comprovante'}
      width={720}
      onClose={onClose}
      footer={
        editing ? (
          <>
            <button type="button" className="btn" onClick={() => setEditing(false)} disabled={saving}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={() => void saveEdit()} disabled={saving}>
              {saving ? <span className="spinner" /> : <IconCheck />} Salvar
            </button>
          </>
        ) : (
          <>
            {imported ? (
              <span className="muted" style={{ fontSize: 12 }}>
                {crypto ? 'Parcela em cripto: para tirar, desmarque a parcela em Vendas em cripto.' : `Venda da ${importedFrom}: sai sozinha se for reembolsada.`}
              </span>
            ) : (
              <button type="button" className="btn btn-danger" onClick={() => setConfirmingDelete(true)}>
                <IconTrash /> Excluir
              </button>
            )}
            <div className="spacer" />
            <button type="button" className="btn" onClick={onClose}>Fechar</button>
            <button type="button" className="btn btn-primary" onClick={startEditing}>
              <IconEdit /> Editar
            </button>
          </>
        )
      }
    >
      {editing ? (
        <>
          <ReceiptFields
            draft={draft}
            onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
            isAdmin={isAdmin}
            autoFocus
            locked={imported}
            {...(crypto ? { lockedNote: 'Parcela em cripto: só a observação e o cliente mudam aqui. Valor, data e dono vêm do carnê.' } : {})}
          />
          {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
        </>
      ) : (
        <>
          <header>
            <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em' }}>
              {receipt.description}
            </div>
            <div
              className="muted"
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px', marginTop: 7, fontSize: 12.5 }}
            >
              <Avatar name={receipt.memberName ?? '?'} color={receipt.memberColor} photo={receipt.memberPhotoUrl} size={20} />
              <span>{receipt.memberName ?? '—'}</span>
              <span aria-hidden>·</span>
              <span className="num">{formatDate(receipt.date)} às {receipt.time}</span>
              <span aria-hidden>·</span>
              <span className="chip">
                {receipt.leverageType ? LEVERAGE_LONG_LABELS[receipt.leverageType] : 'Tipo não informado'}
              </span>
              {imported ? <span className="chip chip-primary">{importedFrom}</span> : null}
              {isReviewed(receipt) || receipt.adminConfirmed ? <span className={REVIEW_CHIP[reviewViewOf(receipt)]}>{REVIEW_LABELS[reviewViewOf(receipt)]}</span> : null}
              {receipt.leadName ? (
                <>
                  <span aria-hidden>·</span>
                  <span>Cliente: {receipt.leadName}</span>
                </>
              ) : null}
            </div>
          </header>

          {/* A venda anexada passa pela conferência do administrador; o membro vê a situação e o motivo. O pedido do checkout comprovado pelo admin mostra a comprovação, sem decisão. */}
          {isReviewed(receipt) || receipt.adminConfirmed ? (
            <ReviewPanel receipt={receipt} canDecide={isAdmin && isReviewed(receipt)} onChange={setReceipt} />
          ) : null}

          {/* Depósito sem tipo: pendente até alguém dizer se foi de boas-vindas ou de evento. */}
          {isDeposit(receipt.leverageType) && !imported ? (
            <ClassifyDepositPanel receipt={receipt} isAdmin={isAdmin} saving={saving} onSave={save} />
          ) : null}

          {alavancagem ? (
            <div className="grid grid2" style={{ gap: 18 }}>
              <StatLine
                label="Valor depositado"
                value={receipt.depositAmount > 0 ? formatBRL(receipt.depositAmount) : '—'}
                sub={receipt.depositAmount > 0 ? 'Entra no capital do cliente' : 'Não informado'}
              />
              <StatLine
                label="Alavancagem buscada"
                value={soughtAmount !== null ? formatBRL(soughtAmount) : '—'}
                sub={
                  soughtAmount !== null
                    ? 'Não entra no valor vendido'
                    : leverageStatus === 'DONE'
                      ? 'Falta informar quanto foi buscado'
                      : 'Entra quando a alavancagem for realizada'
                }
              />
            </div>
          ) : null}

          {alavancagem ? (
            <div className="receipt-status-grid">
              <section className="receipt-status" aria-label="Alavancagem">
                <StatLine
                  label="Alavancagem"
                  value={LEVERAGE_STATUS_LABELS[leverageStatus]}
                  color={LEVERAGE_STATUS_VIEW[leverageStatus].color}
                  sub={LEVERAGE_STATUS_VIEW[leverageStatus].sub}
                />
                {imported ? null : markingDone ? (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ width: 210 }}>
                      <Field label="Alavancagem buscada (R$)">
                        <MoneyInput value={sought} onChange={setSought} autoFocus onEnter={() => void confirmLeverageDone()} />
                      </Field>
                    </div>
                    <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void confirmLeverageDone()}>
                      {saving ? <span className="spinner" /> : <IconCheck />} Confirmar
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => setMarkingDone(false)}>Cancelar</button>
                  </div>
                ) : (
                  <div className="receipt-status-actions">
                    {/* Realizada antes da regra, sem a buscada: dá para informar depois. */}
                    {leverageStatus === 'DONE' && soughtAmount === null ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => {
                          setSought(0);
                          setMarkingDone(true);
                        }}
                      >
                        <IconCheck /> Informar a buscada
                      </button>
                    ) : null}
                    {(['DONE', 'FAILED', 'PENDING'] as const)
                      .filter((status) => status !== leverageStatus)
                      .map((status) => (
                        <button
                          key={status}
                          type="button"
                          className={status === 'DONE' ? 'btn btn-sm' : 'btn btn-sm btn-ghost'}
                          disabled={saving}
                          onClick={() => {
                            // Realizada pede a buscada; as outras zeram a buscada no servidor.
                            if (status === 'DONE') {
                              setSought(0);
                              setMarkingDone(true);
                              return;
                            }
                            void save({ leverageStatus: status }, LEVERAGE_ACTIONS[status].done);
                          }}
                        >
                          {status === 'DONE' ? <IconCheck /> : null} {LEVERAGE_ACTIONS[status].label}
                        </button>
                      ))}
                  </div>
                )}
              </section>
              <section className="receipt-status" aria-label="Repasse">
                <StatLine
                  label="Repasse"
                  value={receipt.transferStatus === 'DONE' ? formatBRL(receipt.transferAmount) : TRANSFER_LABELS[receipt.transferStatus]}
                  color={
                    receipt.transferStatus === 'DONE'
                      ? 'var(--success)'
                      : receipt.transferStatus === 'PENDING'
                        ? 'var(--warning)'
                        : undefined
                  }
                  sub={TRANSFER_SUBTITLE[receipt.transferStatus]}
                />
                {transferActions}
              </section>
            </div>
          ) : gas ? (
            <div className="grid grid3" style={{ gap: 18 }}>
              <StatLine label="Valor do gás" value={formatBRL(receipt.soldAmount)} sub="Fora do vendido e do salário" />
            </div>
          ) : (
            <div className="grid grid3" style={{ gap: 18 }}>
              {transfer ? null : countsAsSold(receipt.leverageType) ? (
                <StatLine label="Vendido" value={formatBRL(receipt.soldAmount)} color="var(--emphasis)" />
              ) : (
                <StatLine
                  label={isDeposit(receipt.leverageType) || isUnaDeposit(receipt.leverageType) ? 'Valor depositado' : isAlavancagem(receipt.leverageType) ? 'Alavancagem buscada' : 'Valor'}
                  value={formatBRL(receipt.soldAmount)}
                  sub="Não entra no valor vendido"
                />
              )}
              {/* O campo "alavancagem colocada" saiu do formulário: só aparece nos comprovantes antigos que têm. */}
              {!transfer && receipt.leverageAmount > 0 ? (
                <StatLine label="Alavancagem colocada" value={formatBRL(receipt.leverageAmount)} sub="Campo antigo" />
              ) : null}
              <StatLine
                label="Repasse"
                value={receipt.transferStatus === 'DONE' ? formatBRL(receipt.transferAmount) : TRANSFER_LABELS[receipt.transferStatus]}
                color={
                  receipt.transferStatus === 'DONE'
                    ? 'var(--success)'
                    : receipt.transferStatus === 'PENDING'
                      ? 'var(--warning)'
                      : undefined
                }
                sub={TRANSFER_SUBTITLE[receipt.transferStatus]}
              />
            </div>
          )}

          {imported || gas || alavancagem ? null : transferActions}

          <section>
            <SectionHead title={`Arquivos · ${count} de ${MAX_FILES}`}>

              {count < MAX_FILES ? (
                <button type="button" className="btn btn-sm" disabled={uploading} onClick={() => fileInput.current?.click()}>
                  {uploading ? <span className="spinner" /> : <IconPlus />} Adicionar arquivo
                </button>
              ) : null}
            </SectionHead>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={ACCEPT_ATTRIBUTE}
              hidden
              onChange={(event) => {
                const chosen = Array.from(event.currentTarget.files ?? []);
                event.currentTarget.value = '';
                void upload(chosen);
              }}
            />
            {count === 0 ? (
              <div className="alert alert-info">
                {crypto
                  ? 'Parcela marcada como paga no carnê. Anexe o print do saque se quiser guardar a prova.'
                  : imported
                  ? 'Venda paga pelo link e confirmada na Ephra: o pagamento já é o comprovante. Anexe um arquivo só se quiser.'
                  : 'Este comprovante veio de um depósito antigo e ainda não tem arquivo. Anexe o print ou o PDF quando tiver.'}
              </div>
            ) : (
              <div className="file-grid">
                {receipt.attachments.map((attachment) => (
                  <AttachmentTile
                    key={attachment.id}
                    receiptId={receipt.id}
                    attachment={attachment}
                    removable={count > 1}
                    busy={removeAttachment.isPending}
                    onRemove={() => void detach(attachment)}
                  />
                ))}
              </div>
            )}
          </section>

          {receipt.note ? (
            <div>
              <div className="label" style={{ marginBottom: 4 }}>Observação</div>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>{receipt.note}</p>
            </div>
          ) : null}
        </>
      )}
    </Modal>
  );
}
